import { formatDateTime, toIso } from '../../lib/formatDate';
import { useAnchorCell } from './anchorCell';

/**
 * A date in the viewer's local timezone, monospaced so columns align — reference §4.
 * Always wrapped in `<time>` so the machine-readable instant travels with the display text.
 *
 * In the anchor column the value darkens, exactly as `<TextCell>` does there: the anchor is
 * the row's click target, and a table whose first column happens to be a date (the audit
 * trail) must not lose that affordance.
 */
export function DateCell({
  value,
  fallback = 'Never',
  interactive = false,
}: {
  value: string | null | undefined;
  fallback?: string;
  /**
   * This stamp is itself a click target that is NOT the anchor — the `Latest update` cell,
   * which opens the audit entry behind it.
   *
   * It takes the product's plain clickable treatment, `font-medium text-slate-800`, rather
   * than the anchor's blue, which is scoped to the row's own subject and must not spread.
   * The tone is SWAPPED here rather than appended by a caller: two tone utilities in one
   * class string are one property at one specificity, resolved by Tailwind's emission order.
   */
  interactive?: boolean;
}) {
  const anchor = useAnchorCell();

  if (!value) {
    return <span className="text-xs text-slate-400">{fallback}</span>;
  }

  const tone = anchor
    ? 'font-medium text-blue-500'
    : interactive
      ? 'font-medium text-slate-800'
      : 'text-slate-500';

  return (
    <time dateTime={toIso(value)} className={`block font-mono tabular-nums text-xs ${tone}`}>
      {formatDateTime(value)}
    </time>
  );
}
